import type { ClaimLaborLineRow, ClaimPartLineRow } from './claimIntakeUtils';
import { sanitizeDecimalInput } from './repairsDocsTabValidation';

function parseNumericField(v: string): number {
  const n = Number.parseFloat(String(v).replace(/[$,]/g, '').trim());
  return Number.isFinite(n) ? n : 0;
}

function roundAmountToTwoDecimals(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
}

function formatAmountString(n: number): string {
  if (!Number.isFinite(n) || n === 0) return '';
  return roundAmountToTwoDecimals(n).toFixed(2);
}

function formatAmountDisplay(value: unknown): string {
  if (value == null) return '';
  const raw = typeof value === 'string' ? value.trim() : String(value).trim();
  if (!raw) return '';
  const n = Number.parseFloat(raw.replace(/[$,]/g, ''));
  if (!Number.isFinite(n)) return raw;
  return roundAmountToTwoDecimals(n).toFixed(2);
}

function formatLaborHoursString(hours: number): string {
  if (!Number.isFinite(hours) || hours <= 0) return '';
  return String(Math.round(hours * 10000) / 10000);
}

/** UI string for quantity × unit price (parts) or hours × rate (labor). */
export function calcLineAmountString(qty: string, unitPrice: string): string {
  const q = parseNumericField(qty);
  const p = parseNumericField(unitPrice);
  if (!qty.trim() || !unitPrice.trim()) return '';
  return formatAmountDisplay(q * p);
}

export function calculateLaborAmount(laborRate: string, hours: string): number {
  const rate = parseNumericField(laborRate);
  const hrs = parseNumericField(hours);
  if (!laborRate.trim() || !hours.trim()) return 0;
  return roundAmountToTwoDecimals(hrs * rate);
}

export function calculateLaborHours(
  amount: string,
  laborRate: string,
  existingHours: string
): number {
  const rate = parseNumericField(laborRate);
  if (!laborRate.trim() || rate <= 0) {
    return parseNumericField(existingHours);
  }
  const amt = parseNumericField(amount);
  if (!amount.trim()) return parseNumericField(existingHours);
  return roundAmountToTwoDecimals(amt / rate);
}

export function calculateLaborLineAmount(unitPrice: string, qty: string): number {
  const price = parseNumericField(unitPrice);
  const quantity = parseNumericField(qty);
  if (!unitPrice.trim() || !qty.trim()) return 0;
  return roundAmountToTwoDecimals(price * quantity);
}

export function calculateLaborLineUnitPrice(
  amount: string,
  qty: string,
  existingUnitPrice: string
): number {
  const quantity = parseNumericField(qty);
  if (!qty.trim() || quantity <= 0) {
    return parseNumericField(existingUnitPrice);
  }
  const amt = parseNumericField(amount);
  if (!amount.trim()) return parseNumericField(existingUnitPrice);
  return roundAmountToTwoDecimals(amt / quantity);
}

/** Derives labor hours from a known line amount and labor rate (API amount, user-entered rate). */
export function calcLaborHoursFromAmountString(amount: string, laborRate: string): string {
  const hours = calculateLaborHours(amount, laborRate, '');
  return formatLaborHoursString(hours);
}

/** Derives labor rate from a known line amount and hours (API amount, user-entered hours). */
export function calcLaborRateFromAmountString(amount: string, hours: string): string {
  const a = parseNumericField(amount);
  const h = parseNumericField(hours);
  if (!amount.trim() || !hours.trim() || h <= 0) return '';
  return formatAmountDisplay(a / h);
}

/** Derives part unit price from a known line amount and quantity. */
export function calcUnitPriceFromAmountString(amount: string, qty: string): string {
  const unitPrice = calculateLaborLineUnitPrice(amount, qty, '');
  return formatAmountString(unitPrice);
}

export function isAmountOnlyLaborLine(row: ClaimLaborLineRow): boolean {
  const amountStr = row.labor_amount.trim();
  const hoursStr = row.labor_hours.trim();
  return Boolean(amountStr && parseNumericField(amountStr) > 0 && (!hoursStr || parseNumericField(hoursStr) <= 0));
}

export type LaborFieldChangeContext = {
  laborRate: string;
  isAmountOnlyPending: boolean;
  hoursDerivationActive: boolean;
  fixedAmount: string | undefined;
  onLaborRateChange?: (v: string) => void;
};

/**
 * When labor rate is entered or changes:
 * - Skips lines with allowFixedValue
 * - fixed amount map (PDF amount-only) → back-calculate hours
 * - amount present, hours missing → back-calculate hours (amount ÷ rate)
 * - hours present → forward-calculate amount (hours × rate)
 */
export function recalcLaborLinesForRateChange(
  laborLines: ClaimLaborLineRow[],
  laborRate: string,
  options?: { fixedAmountByLineId?: ReadonlyMap<number, string> }
): ClaimLaborLineRow[] {
  const rate = laborRate.trim();
  const rateNum = parseNumericField(rate);
  if (!rate || rateNum <= 0) return laborLines;

  return laborLines.map((row) => {
    if (row.allowFixedValue) return row;

    const fixedAmount = options?.fixedAmountByLineId?.get(row.id);
    if (fixedAmount) {
      const computedHours = calcLaborHoursFromAmountString(fixedAmount, rate);
      return computedHours ? { ...row, labor_hours: computedHours, labor_amount: fixedAmount } : row;
    }

    const amountStr = row.labor_amount.trim();
    const hoursStr = row.labor_hours.trim();
    const hoursNum = parseNumericField(hoursStr);

    if (amountStr && (!hoursStr || hoursNum <= 0)) {
      const computedHours = calcLaborHoursFromAmountString(amountStr, rate);
      return computedHours ? { ...row, labor_hours: computedHours } : row;
    }

    if (hoursStr && hoursNum > 0) {
      const computedAmount = calcLineAmountString(hoursStr, rate);
      return computedAmount ? { ...row, labor_amount: computedAmount } : row;
    }

    return row;
  });
}

/** Labor line: amount = labor rate × hours; amount-only PDF lines keep existing derivation rules. */
export function handleLaborFieldChange(
  row: ClaimLaborLineRow,
  field: keyof ClaimLaborLineRow,
  rawValue: string,
  ctx: LaborFieldChangeContext
): ClaimLaborLineRow {
  if (field === 'id') return row;

  if (field === 'allowFixedValue') {
    const nextAllowFixed = rawValue === 'true';
    const next: ClaimLaborLineRow = { ...row, allowFixedValue: nextAllowFixed };
    if (!nextAllowFixed) {
      next.labor_amount = calcLineAmountString(next.labor_hours, ctx.laborRate);
    }
    return next;
  }

  const nextValue =
    field === 'labor_hours' || field === 'labor_amount' ? sanitizeDecimalInput(rawValue) : rawValue;

  if (row.allowFixedValue) {
    return { ...row, [field]: nextValue };
  }

  const updated = { ...row, [field]: nextValue };

  if (field === 'labor_amount') {
    if (!ctx.isAmountOnlyPending) {
      const rateStr = ctx.laborRate.trim();
      if (rateStr && parseNumericField(rateStr) > 0) {
        const computedHours = calcLaborHoursFromAmountString(nextValue, rateStr);
        if (computedHours) updated.labor_hours = computedHours;
      }
    }
    return updated;
  }

  if (field === 'labor_hours') {
    if (ctx.hoursDerivationActive && ctx.fixedAmount) {
      updated.labor_amount = ctx.fixedAmount;
      const derivedRate = calcLaborRateFromAmountString(ctx.fixedAmount, nextValue);
      if (derivedRate) ctx.onLaborRateChange?.(derivedRate);
    } else {
      updated.labor_amount = calcLineAmountString(nextValue, ctx.laborRate);
    }
    return updated;
  }

  return updated;
}

/** Part line: amount = unit price × qty. */
export function handleLaborLineFieldChange(
  row: ClaimPartLineRow,
  field: keyof ClaimPartLineRow,
  rawValue: string,
  _allowFixedValue: boolean
): ClaimPartLineRow {
  if (field === 'id') return row;

  const nextValue =
    field === 'part_quantity' || field === 'part_unit_price' || field === 'part_amount'
      ? sanitizeDecimalInput(rawValue)
      : rawValue;
  const updated = { ...row, [field]: nextValue };

  if (field === 'part_amount') {
    const qtyStr = updated.part_quantity.trim();
    if (qtyStr && parseNumericField(qtyStr) > 0) {
      const computedUnitPrice = calcUnitPriceFromAmountString(nextValue, qtyStr);
      if (computedUnitPrice) updated.part_unit_price = computedUnitPrice;
    }
    return updated;
  }

  if (field === 'part_quantity' || field === 'part_unit_price') {
    updated.part_amount = calcLineAmountString(
      field === 'part_quantity' ? nextValue : updated.part_quantity,
      field === 'part_unit_price' ? nextValue : updated.part_unit_price
    );
    return updated;
  }

  return updated;
}

/** Resolved display / edit value for a labor line amount column. */
export function resolveLaborLineAmountDisplay(
  row: ClaimLaborLineRow,
  laborRate: string
): string {
  if (row.labor_amount.trim()) return row.labor_amount;
  return calcLineAmountString(row.labor_hours, laborRate);
}

/** Resolved display value for a part line amount column. */
export function resolvePartLineAmountDisplay(row: ClaimPartLineRow): string {
  if (row.part_amount.trim()) return row.part_amount;
  return calcLineAmountString(row.part_quantity, row.part_unit_price);
}
